import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppSelector} from "../../store/hooks";
import {leadModalZindex} from "../constants/lead/lead_modal_zindex";
import {useApplicationModalArray} from "../hooks/use_application_form_modal_array";
import {useLeadRequiredParams} from "../hooks/use_lead_required_params";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {RecommendationSource} from "../types/lead/RecommendationSource";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSource} from "../utils/form/application_form_source";
import {ApplicationGoodbyeContentModal} from "./goodbye_content_modal/ApplicationGoodbyeContentModal";
import {LeadFormModalAskForFullOffer} from "./lead_form_modal/LeadFormModalAskForFullOffer";
import {LeadFormModalStandard} from "./lead_form_modal/LeadFormModalStandard";
import {LeadFormModalWithNegotiation} from "./lead_form_modal/LeadFormModalWithNegotiation";
import {LeadFormModalWithTopic} from "./lead_form_modal/LeadFormModalWithTopic";
import {ApplicationMultileadModalStep} from "./multi_lead/ApplicationMultileadModalStep";
import {ApplicationNotificationConsentConfirmationModal} from "./notification_consent_confirmation_modal/ApplicationNotificationConsentConfirmationModal";
import {ApplicationSuccessInfoModal} from "./success_info_modal/ApplicationSuccessInfoModal";

export const ApplicationModalArray = () => {
    const {propertyId} = useLeadRequiredParams();

    const {modalState, sendModalEvent, isOpened, source, initialLeadFormValues, leadData} = useApplicationModalArray();

    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    const roomCount = leadData?.property?.detail?.rooms as number;
    const propertyPrice = (leadData?.property?.detail?.price ?? null) as number | null;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = leadData?.application || null;

    const onModalClose = () => {
        sendModalEvent({type: "FORM_CLOSED"});
    };

    const onSingleLeadSuccess = async (
        sentApplication: IApplicationData | null,
        _offer?: IOfferDetailApplication,
        _property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        const needsMarketingConsent = !!sentApplication && !sentApplication.marketing_email && !sentApplication.marketing_sms;
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;

        sendModalEvent({type: "FORM_SUBMITTED"}, {originalApplicationResponse: sentApplication, recommendationsWithPriceFilter}, {needsMarketingConsent});
    };

    // Render
    const renderContent = () => {
        switch (modalState.step) {
            case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
                return initialLeadFormValues ? (
                    <LeadFormModalStandard
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        initialLeadFormValues={initialLeadFormValues}
                    />
                ) : null;
            }
            case ApplicationModalStep.APPLICATION_FORM_FULL_OFFER: {
                return (
                    <LeadFormModalAskForFullOffer
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        initialLeadFormValues={initialLeadFormValues}
                    />
                );
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return (
                    <LeadFormModalWithNegotiation
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        initialLeadFormValues={initialLeadFormValues}
                        price={propertyPrice}
                    />
                );
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return (
                    <LeadFormModalWithTopic
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        initialLeadFormValues={initialLeadFormValues}
                    />
                );
            }
            case ApplicationModalStep.NOTIFICATION_CONSENT: {
                return (
                    <ApplicationNotificationConsentConfirmationModal
                        result={modalState.data.originalApplicationResponse}
                        onConsentComplete={() => sendModalEvent({type: "CONSENT_COMPLETED"})}
                        onModalClose={onModalClose}
                    />
                );
            }
            case ApplicationModalStep.SUCCESS_INFO: {
                return (
                    <ApplicationSuccessInfoModal
                        isModal
                        originalApplicationResponse={modalState.data.originalApplicationResponse}
                        onClick={() => sendModalEvent({type: "SUCCESS_INFO_NEXT"})}
                        shouldRenderRecommendations
                        onModalClose={onModalClose}
                    />
                );
            }
            case ApplicationModalStep.GOODBYE_INFO: {
                return <ApplicationGoodbyeContentModal closeModal={onModalClose} />;
            }
            case ApplicationModalStep.MULTILEAD: {
                if (!isMultileadStepEnabled && initialStep !== ApplicationModalStep.MULTILEAD) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return null;
                }

                return (
                    <ApplicationMultileadModalStep
                        originalApplicationResponse={modalState.data.originalApplicationResponse || originalApplication}
                        recommendationsWithPriceFilter={modalState.data.recommendationsWithPriceFilter}
                        roomCount={roomCount}
                        isAbroad={isAbroad}
                        recommendationSource={recommendationSource}
                        onNoRecommendations={(originalApplicationResponse: IApplicationData | null) => {
                            const needsMarketingConsent =
                                !!originalApplicationResponse && !originalApplicationResponse.marketing_email && !originalApplicationResponse.marketing_sms;
                            sendModalEvent({type: "MULTILEAD_NO_RECOMMENDATIONS"}, {originalApplicationResponse}, {needsMarketingConsent});
                        }}
                        onClose={() => sendModalEvent({type: "MULTILEAD_CLOSED"})}
                        onMultiLeadComplete={(_payload: IApplicationMultiLeadSummaryStepState) => {
                            const app = modalState.data.originalApplicationResponse;
                            const needsMarketingConsent = !!app && !app.marketing_email && !app.marketing_sms;
                            sendModalEvent({type: "MULTILEAD_COMPLETED"}, undefined, {needsMarketingConsent});
                        }}
                    />
                );
            }
            case ApplicationModalStep.WAITING:
            default:
                return (
                    <div className={waitingWrapperStyle}>
                        <Loader size="lg" fullWidth />
                    </div>
                );
        }
    };

    const shouldCloseOnOverlayClick = modalState.step !== ApplicationModalStep.APPLICATION_FORM_STANDARD;

    return (
        <Modal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            zIndex={leadModalZindex}
            showCloseButton={false}
            fullScreen="xs"
        >
            {modalState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ? (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderStyle}>{renderContent()}</div>
                </div>
            ) : (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderFullStyle}>{renderContent()}</div>
                </div>
            )}
        </Modal>
    );
};

const applicationWrapStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%"
});

const applicationHolderStyle = css({
    width: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const applicationHolderFullStyle = css({
    width: "100%",
    height: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const waitingWrapperStyle = css({
    position: "relative",
    md: {
        width: "800px",
        height: "auto"
    }
});
